import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosError } from 'axios'
import axiosRetry from 'axios-retry'
import keycloak from '@/keycloak'
import { DEV_BYPASS_KEYCLOAK, DEV_STATIC_TOKEN } from '@/devAuth'

import { API_BASE_URL, AXIOS_RETRY_COUNT, CUSTOM_JSON_HEADERS } from './consts'
import { AUTH_CONSTANTS } from '@/consts/constants'

/**
 * Raw axios instance without interceptors
 */
export const axiosInstanceRaw: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: CUSTOM_JSON_HEADERS,
})

/**
 * Creates an axios instance with Keycloak authentication
 * @param baseUrl - API base URL (defaults to auth base URL if not provided)
 * @returns Configured axios instance
 */
const createAuthenticatedAxiosInstance = (baseUrl = API_BASE_URL): AxiosInstance => {
  const instance: AxiosInstance = axios.create({
    baseURL: baseUrl,
    headers: CUSTOM_JSON_HEADERS,
  })

  // Request interceptor to add authorization header
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (DEV_BYPASS_KEYCLOAK && DEV_STATIC_TOKEN) {
        config.headers.Authorization = `Bearer ${DEV_STATIC_TOKEN}`
        return config
      }
      // Only add auth header if Keycloak is authenticated
      if (keycloak.authenticated && keycloak.token) {
        try {
          // Refresh token if it expires in less than 30 seconds
          await keycloak.updateToken(AUTH_CONSTANTS.TOKEN_MIN_VALIDITY_SECONDS)

          // Add the current token to the request
          config.headers.Authorization = `Bearer ${keycloak.token}`
        } catch (error) {
          console.error('Failed to refresh token:', error)
          // Redirect to login on token refresh failure
          await keycloak.login()
          throw error
        }
      }
      return config
    },
    (error: AxiosError): Promise<AxiosError> => Promise.reject(error),
  )

  // Response interceptor to handle token expiration
  instance.interceptors.response.use(
    (response) => {
      return response
    },
    async (error) => {
      if (error.response?.status === 401 && !DEV_BYPASS_KEYCLOAK) {
        console.warn('🚨 401 UNAUTHORIZED - Handling token expiration')

        keycloak.clearToken()
        await keycloak.login({
          redirectUri: window.location.origin,
        })
      }
      return Promise.reject(error)
    },
  )

  return instance
}

/**
 * Main authenticated axios instance
 */
export const axiosDZAuthInstance = createAuthenticatedAxiosInstance(API_BASE_URL)

/**
 * Advanced retry configuration with the following benefits:
 * 1. Implements exponential backoff to prevent overwhelming servers during instability
 * 2. Intelligently retries server errors (5xx) which are likely temporary
 * 3. Avoids wasteful retries on client errors (4xx) which typically won't resolve with retries
 * 4. Maintains retry capability for network failures and idempotent requests
 *
 * This approach minimizes user facing failures while preventing unnecessary load on backend services.
 */
axiosRetry(axiosDZAuthInstance, {
  retries: AXIOS_RETRY_COUNT,
  retryDelay: axiosRetry.exponentialDelay, // Use exponential backoff
  retryCondition: (error: AxiosError): boolean => {
    // Don't retry on 401 as this is handled by the interceptor
    if (error.response?.status === 401) {
      return false
    }

    // Only retry on network errors and 5xx responses (not on 4xx client errors)
    return !!(
      axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      (error.response?.status && error.response.status >= 500)
    )
  },
})
